import ProgressIndicator from 'components/common/progress-indicator';
import CheckboxInput from 'components/inputs/checkbox';
import MultiselectInput from 'components/inputs/multiselect';
import NumberInput from 'components/inputs/number';
import SelectInput from 'components/inputs/select';
import TextInput from 'components/inputs/text';
import TextareaInput from 'components/inputs/textarea';
import ToggleSwitch from 'components/inputs/toggle-switch';
import InfoTooltip from 'components/layout/info-tooltip';
import Tooltip from 'components/layout/tooltip';
import { ReactNode } from 'react';
import { isEmpty } from 'utils/helpers';

interface FormInputProps {
  type:
    | 'number'
    | 'select'
    | 'multiselect'
    | 'text'
    | 'textarea'
    | 'toggleSwitch'
    | 'checkbox';
  value: any;
  onChange: (value: any) => void;
  options?: (string | number)[] | { label: string; value: string | number }[];
  showProgress?: boolean;
  completed?: boolean;
  required?: boolean;
  label?: string;
  subLabel?: string;
  debounce?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
  preventDeselection?: boolean;
  enableChips?: boolean;
  placeholder?: string;
  step?: number;
  tooltip?: string;
  tooltipLocation?: 'default' | 'after_label';
  inlineLabel?: boolean;
}

export default function FormInput({
  showProgress,
  completed,
  required,
  type,
  label,
  subLabel,
  tooltip,
  tooltipLocation = 'default',
  inlineLabel = false,
  ...props
}: FormInputProps) {
  let inputElement: ReactNode | null = null;

  const shouldEnableChips = () => {
    if (props.enableChips !== undefined) {
      return props.enableChips;
    }

    return (
      (props.options?.length ?? 0) <= 3 &&
      props.options?.every((o) =>
        typeof o === 'object'
          ? o.value.toString().length <= 20
          : o.toString().length <= 20
      )
    );
  };

  if (type === 'number') {
    inputElement = (
      <NumberInput
        {...props}
        invalid={required && !showProgress && isEmpty(props.value)}
      />
    );
  }

  if (type === 'select') {
    inputElement = (
      <SelectInput
        {...props}
        enableChips={shouldEnableChips()}
        invalid={required && !showProgress && isEmpty(props.value)}
      />
    );
  }

  if (type === 'multiselect') {
    inputElement = (
      <MultiselectInput
        {...props}
        options={(props.options ?? []) as string[]}
        invalid={required && !showProgress && isEmpty(props.value)}
      />
    );
  }

  if (type === 'text') {
    inputElement = (
      <TextInput
        {...props}
        invalid={required && !showProgress && isEmpty(props.value)}
      />
    );
  }

  if (type === 'textarea') {
    inputElement = <TextareaInput {...props} />;
  }

  if (type === 'toggleSwitch') {
    inputElement = <ToggleSwitch {...props} />;
  }

  if (type === 'checkbox') {
    return (
      <label
        className={`flex items-center gap-2 ${
          props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <CheckboxInput {...props} />
        {label && <span className='font-semibold text-gray-800'>{label}</span>}
      </label>
    );
  }

  return (
    <div
      className={
        inlineLabel ? 'flex items-center gap-3' : 'flex flex-col gap-2'
      }
    >
      {(showProgress || label || subLabel) && (
        <div className={inlineLabel ? undefined : 'w-full'}>
          <div
            className={`relative inline-flex items-center gap-2 ${
              tooltip && tooltipLocation === 'default' && 'cursor-help'
            }`}
          >
            {showProgress && (
              <ProgressIndicator required={required} checked={completed} />
            )}
            {label && (
              <span className='font-semibold text-gray-800'>{label}</span>
            )}
            {subLabel && <span className='ml-1 text-gray-500'>{subLabel}</span>}
            {required && !showProgress && (
              <span className='font-semibold text-red-500'>*</span>
            )}
            {tooltip && tooltipLocation === 'default' && (
              <Tooltip text={tooltip} />
            )}
            {tooltip && tooltipLocation === 'after_label' && (
              <InfoTooltip>{tooltip}</InfoTooltip>
            )}
          </div>
        </div>
      )}
      {inputElement ?? 'Invalid input type!'}
    </div>
  );
}
